import React, { useState } from 'react';
import './Stances.css';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// Images (update paths as needed)
import attention from './images/attentionstance.webp';
import ready from './images/readystance.webp';
import walking from './images/walkingstance.webp';
import sitting from './images/sittingstance.webp';
import lstance from './images/lstance.webp';
import closed from './images/closedstance.webp';
import fixed from './images/fixedstance.webp';
import bending from './images/bendingstance.webp';
import xstance from './images/xstance.webp';
import rearfoot from './images/rearfootstance.webp';
import low from './images/lowstance.webp';
import vertical from './images/verticalstance.webp';
import oneleg from './images/onelegstance.webp';
import diagonal from './images/diagonalstance.webp';

const stances = [
  {
    title: 'Parallel Ready Stance',
    korean: 'Narani Junbi Sogi',
    image: ready,
    description: [
      'Weight: 50/50',
      'Width: 1 shoulder width',
      'Facing: Full or Half',
      'L/R: Both'
    ]
  },
  {
    title: 'Walking Stance',
    korean: 'Gunnun Sogi',
    image: walking,
    description: [
      'Weight: 50/50',
      'Width: 1 shoulder width between insteps',
      'Length: 1½ shoulder width between big toes',
      'The front knee bent until the kneecap is inline with the heel',
      'The back leg is not bent and the rear foot is pointed about 15° outwards',
      'Facing: Full or Half',
      'L/R: Leading foot'
    ]
  },
  {
    title: 'Attention Stance',
    korean: 'Charyot Sogi',
    image: attention,
    description: [
      'Weight: 50/50',
      'The heels are touching and toes point out at 45°',
      'Facing front look forward',
      'Arms are slightly bent out from the waist',
      'Fists are slightly clenched',
      'L/R: Both',
      'Bowing (Kyong Ye): Bow at 15°, maintain eye contact.'
    ]
  },
  {
    title: 'Sitting Stance',
    korean: 'Annun Sogi',
    image: sitting,
    description: [
      'Weight: 50/50',
      'Width: 1½ shoulder width between big toes',
      'Facing: Full or Side',
      'Knees bent over foot, back straight',
      'L/R: Side of strike'
    ]
  },
  {
    title: 'L-Stance',
    korean: 'Niunja Sogi',
    image: lstance,
    description: [
      'Weight: 70/30',
      'Width: 2.5cm',
      'Length: 1½ shoulder width from rear footsword to big toe',
      'Both feet are pointed about 15° inwards',
      'The rear knee is bent until the kneecap is inline with the toes',
      'The rear hip is in line with the inner knee joint',
      'Facing: Half',
      'L/R: Most weight'
    ]
  },
  {
    title: 'Closed Stance',
    korean: 'Moa Sogi',
    image: closed,
    description: [
      'Weight: 50/50',
      'Toes are touching',
      'A: 30cm between philtrum and fists',
      'B: 15cm between navel and hands',
      'C: 10cm between abdomen and hands',
      'D: 30cm between fists and thighs',
      'Facing: Full or Side',
      'L/R: Both'
    ]
  },
  {
    title: 'Fixed Stance',
    korean: 'Gojung Sogi',
    image: fixed,
    description: [
      'Weight: 50/50',
      'Width: 2.5cm between heels',
      'Length: 1½ shoulder width between big toes',
      'Both feet point ~15° inwards',
      'Facing: Half',
      'L/R: Lead foot'
    ]
  },
  {
    title: 'Bending Ready Stance',
    korean: 'Guburyo Sogi',
    image: bending,
    description: [
      'Weight: 100/0',
      'Standing leg is bent slightly',
      'Other foot lifted in front of opposite knee',
      'Facing: Full or Half',
      'L/R: Most weight'
    ]
  },
  {
    title: 'X Stance',
    korean: 'Kyocha Sogi',
    image: xstance,
    description: [
      'Weight: 95/05',
      'Width: 1 shoulder width between small toes',
      'Rear knee points slightly inward',
      'Facing: Full, Half or Side',
      'L/R: Most weight'
    ]
  },
  {
    title: 'Rear Foot Stance',
    korean: 'Dwitbal Sogi',
    image: rearfoot,
    description: [
      'Weight: 90/10',
      'Heels should be inline',
      'Length: 1 shoulder width',
      'Facing: Half',
      'L/R: Most weight'
    ]
  },
  {
    title: 'Low Stance',
    korean: 'Nachou Sogi',
    image: low,
    description: [
      'Weight: 50/50',
      'Width: 1 shoulder width between insteps',
      'Length: 1½ shoulder width + 1 foot length between big toes',
      'Facing: Full or Side',
      'L/R: Leading foot'
    ]
  },
  {
    title: 'Vertical Stance',
    korean: 'Soojik Sogi',
    image: vertical,
    description: [
      'Weight: 60/40',
      'Heels should be inline',
      'Length: 1 shoulder width between big toes',
      'Feet point ~15° inwards',
      'Facing: Half',
      'L/R: Most weight'
    ]
  },
  {
    title: 'One-Leg Stance',
    korean: 'Waebal Sogi',
    image: oneleg,
    description: [
      'Weight: 100/0',
      'Standing leg is straight',
      'Other foot is lifted to the knee',
      'Facing: Full or Side',
      'L/R: Most weight'
    ]
  }, 
  {
    title: 'Diagonal Stance',
    korean: 'Sasun Sogi',
    image: diagonal,
    description: [
      'Weight: 50/50',
      'Width: 1½ shoulder width',
      'Length: 1 foot width between big toes',
      'Heel of front foot inline with toes of rear foot',
      'Facing: Full or Side',
      'L/R: Leading foot'
    ]
  }
];

const Stances = () => {
  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate('/');
  };
  
  return (
    <>
      <Helmet>
        <title>TKD Study - Stances</title>
      </Helmet>
      <div className="stances-page">
        <h1>Stances</h1>
        <div className="back-arrow" onClick={() => navigate('/')}>
          ← Back
        </div>
        {stances.map((stance, i) => (
          <StanceCard key={i} {...stance} />
        ))}
      </div>
    </>
  );
};


const StanceCard = ({ title, korean, description, image }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="stance-card">
      <div className={`stance-text ${expanded ? 'expanded' : ''}`}>
        <h2>{title}</h2>
        <p className="korean-name">{korean}</p>
        <ul>
          {description.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      </div>
      <img
        src={image}
        alt={title}
        className="stance-image"
        onClick={() => setExpanded(!expanded)}
      />
    </div>
  );
};

export default Stances;
